// src/pages/TeacherLessons.js
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getLessonsForTeacher, getMyCourses } from '../../../api/teacherApi';
import './TeacherLessons.css';

function TeacherLessons() {
  const [lessons, setLessons] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    course: 'all',
    status: 'all',
    dateRange: 'all'
  });
  const [view, setView] = useState('list'); // 'list' or 'calendar'

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [lessonsData, coursesData] = await Promise.all([
        getLessonsForTeacher(),
        getMyCourses()
      ]);
      
      setLessons(lessonsData);
      setCourses(coursesData);
    } catch (error) {
      setError('Error loading data: ' + (error.response?.data?.msg || error.message));
      console.error('Veriler yüklenirken hata oluştu:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterLessons = () => {
    let filtered = [...lessons];

    // Course filter
    if (filters.course !== 'all') {
      filtered = filtered.filter(lesson => 
        lesson.course && lesson.course._id === filters.course
      );
    }

    // Status filter (past/upcoming)
    const now = new Date();
    if (filters.status === 'upcoming') {
      filtered = filtered.filter(lesson => new Date(lesson.scheduledAt) > now);
    } else if (filters.status === 'past') {
      filtered = filtered.filter(lesson => new Date(lesson.scheduledAt) <= now);
    }

    // Date range filter
    if (filters.dateRange === 'today') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      filtered = filtered.filter(lesson => {
        const lessonDate = new Date(lesson.scheduledAt);
        return lessonDate >= today && lessonDate < tomorrow;
      });
    } else if (filters.dateRange === 'week') {
      const weekStart = new Date();
      weekStart.setHours(0, 0, 0, 0);
      weekStart.setDate(weekStart.getDate() - weekStart.getDay()); // Sunday
      
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 7);
      
      filtered = filtered.filter(lesson => {
        const lessonDate = new Date(lesson.scheduledAt);
        return lessonDate >= weekStart && lessonDate < weekEnd;
      });
    }

    return filtered.sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getLessonStatus = (lessonDate) => {
    const now = new Date();
    const lessonTime = new Date(lessonDate);
    
    if (lessonTime < now) return { status: 'past', text: 'Completed', class: 'past' };
    
    const diffMs = lessonTime - now;
    const diffHours = diffMs / (1000 * 60 * 60);
    
    if (diffHours < 1) {
      return { status: 'soon', text: 'Starting Soon', class: 'soon' };
    }
    if (diffHours < 24) {
      return { status: 'today', text: 'Today', class: 'today' };
    }
    if (diffHours < 48) {
      return { status: 'tomorrow', text: 'Tomorrow', class: 'tomorrow' };
    }
    
    return { status: 'upcoming', text: 'Upcoming', class: 'upcoming' };
  };

  const getTimeUntil = (dateString) => {
    const now = new Date();
    const lessonDate = new Date(dateString);
    const diffMs = lessonDate - now;
    
    if (diffMs <= 0) return 'Started';
    
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffDays > 0) return `${diffDays}d ${diffHours}h`;
    if (diffHours > 0) return `${diffHours}h ${diffMins}m`;
    return `${diffMins}m`;
  };

  if (loading) {
    return (
      <div className="teacher-lessons-loading">
        <div className="loading-spinner">⏰</div>
        <p>Loading lesson schedule...</p>
      </div>
    );
  }

  const filteredLessons = filterLessons();

  return (
    <div className="teacher-lessons">
      {/* Header */}
      <div className="lessons-header">
        <div className="header-content">
          <h1>⏰ Lesson Schedule</h1>
          <p>Track and manage all your lessons</p>
        </div>
        <div className="header-actions">
          <button onClick={loadData} className="refresh-btn" disabled={loading}>
            <span className="refresh-icon">🔄</span>
            Refresh
          </button>
          <Link to="/teacher/add-lesson" className="create-btn">
            ➕ Add Lesson
          </Link>
        </div>
      </div>

      {error && (
        <div className="lessons-error-banner">
          <span>⚠️</span>
          <span>{error}</span>
          <button onClick={() => setError('')} className="close-btn">×</button>
        </div>
      )}

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-group">
          <label>Course:</label>
          <select 
            value={filters.course}
            onChange={(e) => setFilters(prev => ({ ...prev, course: e.target.value }))}
          >
            <option value="all">All Courses</option>
            {courses.map(course => (
              <option key={course._id} value={course._id}>{course.title}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Status:</label>
          <select 
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
          >
            <option value="all">All Lessons</option>
            <option value="upcoming">Upcoming Lessons</option>
            <option value="past">Past Lessons</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Date:</label>
          <select 
            value={filters.dateRange}
            onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value }))}
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
          </select>
        </div>

        <div className="view-toggle">
          <button 
            className={view === 'list' ? 'active' : ''}
            onClick={() => setView('list')}
          >
            ☰ List
          </button>
          <button 
            className={view === 'calendar' ? 'active' : ''}
            onClick={() => setView('calendar')}
          >
            📅 Calendar
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="lessons-stats">
        <div className="stat-card">
          <span className="stat-value">{filteredLessons.length}</span>
          <span className="stat-label">Total Lessons</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{filteredLessons.filter(l => getLessonStatus(l.scheduledAt).status === 'upcoming' || getLessonStatus(l.scheduledAt).status === 'today' || getLessonStatus(l.scheduledAt).status === 'tomorrow').length}</span>
          <span className="stat-label">Scheduled</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{filteredLessons.filter(l => getLessonStatus(l.scheduledAt).status === 'soon').length}</span>
          <span className="stat-label">Starting Soon</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{filteredLessons.filter(l => getLessonStatus(l.scheduledAt).status === 'past').length}</span>
          <span className="stat-label">Completed</span>
        </div>
      </div>

      {/* Lessons List */}
      {filteredLessons.length === 0 ? (
        <div className="empty-lessons">
          <div className="empty-icon">📅</div>
          <h3>No lessons found</h3>
          <p>No lessons match your filter criteria</p>
        </div>
      ) : view === 'list' ? (
        <div className="lessons-list">
          {filteredLessons.map(lesson => {
            const status = getLessonStatus(lesson.scheduledAt);
            
            return (
              <div key={lesson._id} className={`lesson-item ${status.class}`}>
                <div className="lesson-main">
                  <div className="lesson-info">
                    <h3>{lesson.title}</h3>
                    <p className="course-name">{lesson.course?.title}</p>
                    <p className="lesson-time">{formatDate(lesson.scheduledAt)}</p>
                    <p className="lesson-duration">{lesson.durationMinutes} minutes</p>
                  </div>
                  
                  <div className="lesson-status">
                    <span className={`status-badge ${status.class}`}>
                      {status.text}
                    </span>
                    {status.status !== 'past' && (
                      <span className="time-until">
                        {getTimeUntil(lesson.scheduledAt)}
                      </span>
                    )}
                  </div>
                </div>

                <div className="lesson-actions">
                  {lesson.zoomLink && status.status !== 'past' && (
                    <a href={lesson.zoomLink} target="_blank" rel="noopener noreferrer" className="join-btn">
                      🔗 Join Zoom
                    </a>
                  )}
                  <button className="edit-btn" title="Edit">
                    ✏️ Edit
                  </button>
                  <button className="details-btn" title="Details">
                    ℹ️ Details
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="calendar-view">
          <div className="calendar-placeholder">
            <div className="calendar-icon">📅</div>
            <h3>Calendar View</h3>
            <p>Calendar view coming soon</p>
            <button onClick={() => setView('list')} className="switch-view-btn">
              Switch to List View
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default TeacherLessons;